import { OFFICE_365_TOKEN_PATH } from '@my-backoffice/app/pages/login/federated/office365/constants/office365-auth.constants';
import { Office365AuthenticationService } from '@my-backoffice/app/pages/login/federated/office365/services/office365-authentication.service';
import { AppOverlayService } from '@my-backoffice/app/shared/services/modal-services/app-overlay/app-overlay.service';
import { AppSnackbarService } from '@my-backoffice/app/shared/services/modal-services/app-snackbar/app-snackbar.service';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { inject, Injectable, Injector } from '@angular/core';
import { EQimaSnackbarType } from '@qima/ngx-qima';
import { Observable, throwError, timer } from 'rxjs';
import { catchError } from 'rxjs/operators';

@Injectable()
export class Office365ErrorHandlerInterceptor implements HttpInterceptor {
  private _isLoginExpiredShown = false;
  private readonly _injector = inject(Injector);

  public constructor(
    private readonly _overlayService: AppOverlayService,
    private readonly _appSnackbarService: AppSnackbarService
  ) {}

  private get _authenticationService(): Office365AuthenticationService {
    return this._injector.get(Office365AuthenticationService);
  }

  public intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse): Observable<never> => {
        if (error.status === 401) {
          if (req.url.includes(OFFICE_365_TOKEN_PATH) && req.method === 'PUT') {
            if (!this._isLoginExpiredShown) {
              this._isLoginExpiredShown = true;
              this._appSnackbarService.showSnackbar('COMMON.LOGIN_EXPIRED', EQimaSnackbarType.ERROR, 5000);

              timer(3000).subscribe((): void => {
                this._authenticationService.logout();
              });
            }
          } else if (req.url.includes(OFFICE_365_TOKEN_PATH) && req.method === 'DELETE') {
            // no-op for logout token delete
          } else {
            this._authenticationService.refreshAccessToken();
          }
        } else {
          const parsedError = this._parseErrorResponse(error);
          const errorCode = parsedError?.code;

          if (errorCode !== 4200 && (!req.headers.has('skipErrorHandler') || req.headers.get('skipErrorHandler') === 'false')) {
            const errorMessage = this._getErrorMessage(parsedError);

            if (req.headers.has('reqCode') && error.status === parseInt(req.headers.get('reqCode') as string, 10)) {
              this._appSnackbarService.showSnackbar(req.headers.get('errorMessage') || errorMessage || 'COMMON.SERVICE_ERROR', EQimaSnackbarType.ERROR, 5000);
            } else {
              this._appSnackbarService.showSnackbar(errorMessage || 'COMMON.SERVICE_ERROR', EQimaSnackbarType.ERROR, 5000);
            }
          }

          this._overlayService.updateIsLoading(false);
          throw error;
        }

        this._overlayService.updateIsLoading(false);

        return throwError((): HttpErrorResponse => error);
      })
    );
  }

  private _parseErrorResponse(error: HttpErrorResponse): Record<string, unknown> | null {
    if (!error.error) {
      return null;
    }

    if (typeof error.error === 'object' && error.error !== null) {
      return error.error as Record<string, unknown>;
    }

    if (typeof error.error === 'string') {
      try {
        return JSON.parse(error.error) as Record<string, unknown>;
      } catch {
        return null;
      }
    }

    return null;
  }

  private _getErrorMessage(parsedError: Record<string, unknown> | null): string | undefined {
    if (!parsedError) {
      return undefined;
    }

    const { message, msg } = parsedError;

    if (typeof message === 'string' && message) {
      return message;
    }

    if (typeof msg === 'string' && msg) {
      return msg;
    }

    return undefined;
  }
}
